import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

// AdSense type definition
declare global {
  interface Window {
    adsbygoogle: any[];
  }
}

interface AdComponentProps {
  adCode: string;
  clickUrl?: string; // Reklama tıklandığında açılacak link (opsiyonel)
  containerStyles?: React.CSSProperties;
  showBorders?: boolean; // Option to show borders for testing
  position?: string; // Opsiyonel olarak reklamın pozisyonunu belirtebiliriz
}

const AdComponent: React.FC<AdComponentProps> = ({ 
  adCode, 
  clickUrl,
  containerStyles, 
  showBorders = false,
  position 
}) => {
  const adContainerRef = useRef<HTMLDivElement>(null);
  const [isTestAd, setIsTestAd] = useState(false);
  const [isUrlAd, setIsUrlAd] = useState(false);
  const previousAdCodeRef = useRef<string>('');

  // Reklamın konumuna göre konsola bilgi yazdır
  useEffect(() => {
    if (position) {
      console.log(`Reklam yükleniyor, pozisyon: ${position}, kod: ${adCode.substring(0, 50)}...`);
      console.log(`ClickUrl:`, clickUrl);
    }
  }, [position, adCode, clickUrl]);

  useEffect(() => {
    // Skip processing if the ad code is the same as before or empty
    if (!adCode || !adContainerRef.current || adCode === previousAdCodeRef.current) return;
    
    // Save the current adCode to prevent re-processing the same ad
    previousAdCodeRef.current = adCode;

    // Check if this appears to be a test ad
    const isTest = adCode.includes('Test Reklam') || adCode.includes('ca-pub-xxxxxxxxxx');
    setIsTestAd(isTest);

    // Check if this is a URL (starts with http:// or https://)
    const isUrl = adCode.trim().startsWith('http://') || adCode.trim().startsWith('https://');
    setIsUrlAd(isUrl);

    if (isUrl) {
      // If it's a URL, create an image or iframe element
      if (adContainerRef.current) {
        // Clear previous content
        adContainerRef.current.innerHTML = '';
        
        // İçerik görünümünü geliştirmek için container div oluştur
        const container = document.createElement('div');
        container.style.width = '100%';
        container.style.margin = '0 auto';
        container.style.textAlign = 'center';
        
        if (adCode.includes('.jpg') || adCode.includes('.jpeg') || adCode.includes('.png') || adCode.includes('.gif')) {
          // Create image for image URLs
          const img = document.createElement('img');
          img.src = adCode;
          img.style.maxWidth = '100%';
          img.style.height = 'auto';
          img.style.margin = '0 auto';
          img.style.display = 'block';
          img.alt = 'Reklam';
          
          // Add click handler to open the click URL or fallback to ad URL
          img.style.cursor = 'pointer';
          img.onclick = () => {
            const targetUrl = clickUrl || adCode;
            console.log('Reklam tıklandı! ClickUrl:', clickUrl, 'AdCode:', adCode, 'Hedef URL:', targetUrl);
            window.open(targetUrl, '_blank');
          };
          
          container.appendChild(img);
        } else {
          // Create iframe for other URLs (like ad services)
          const iframe = document.createElement('iframe');
          iframe.src = adCode;
          iframe.width = '100%';
          iframe.height = position === 'MIDDLE_CONTENT' ? '350px' : '300px'; // İçerik ortası için daha yüksek
          iframe.style.border = 'none';
          iframe.style.display = 'block';
          iframe.style.margin = '0 auto';
          iframe.style.maxWidth = '100%';
          iframe.frameBorder = '0';
          iframe.scrolling = 'no';
          iframe.allowFullscreen = true;
          
          // İframe yüklenmesini izle
          iframe.onload = () => {
            console.log(`İframe yüklendi: ${adCode}`);
          };
          
          container.appendChild(iframe);
          
          // Eğer clickUrl varsa, iframe'in üzerine tıklanabilir overlay ekle
          if (clickUrl) {
            const overlay = document.createElement('div');
            overlay.style.position = 'absolute';
            overlay.style.top = '0';
            overlay.style.left = '0';
            overlay.style.width = '100%';
            overlay.style.height = '100%';
            overlay.style.cursor = 'pointer';
            overlay.style.backgroundColor = 'transparent';
            overlay.style.zIndex = '10';
            overlay.onclick = () => {
              console.log('İframe overlay tıklandı! ClickUrl:', clickUrl);
              window.open(clickUrl, '_blank');
            };
            
            container.style.position = 'relative';
            container.appendChild(overlay);
          }
        }
        
        adContainerRef.current.appendChild(container);
        
        // Debug için görünürlüğü kontrol et
        console.log(`Reklam render edildi (URL): ${position}`, { 
          containerWidth: adContainerRef.current.offsetWidth,
          containerHeight: adContainerRef.current.offsetHeight,
          url: adCode.substring(0, 50) + '...'
        });
      }
    } else {
      // Google Ads kodunu HTML olarak ekleyelim
      adContainerRef.current.innerHTML = adCode;

      // AdSense optimizasyonu: Script injection'ı iyileştir
      const scripts = adContainerRef.current.getElementsByTagName('script');
      if (scripts.length > 0) {
        Array.from(scripts).forEach((oldScript, index) => {
          const newScript = document.createElement('script');
          
          // AdSense script'leri için özel optimizasyon
          if (oldScript.src && oldScript.src.includes('googlesyndication')) {
            // Ana AdSense script'i sadece bir kez yükle
            if (!window.adsbygoogle) {
              newScript.src = oldScript.src;
              newScript.async = true;
            } else {
              return; // Zaten yüklenmişse skip et
            }
          } else {
            // Inline script'ler için öznitelikleri kopyala
            Array.from(oldScript.attributes).forEach(attr => {
              newScript.setAttribute(attr.name, attr.value);
            });
            
            // AdSense push işlemi için timing optimizasyonu
            if (oldScript.innerHTML.includes('adsbygoogle')) {
              const scriptContent = oldScript.innerHTML;
              newScript.appendChild(document.createTextNode(`
                setTimeout(() => {
                  try {
                    ${scriptContent}
                  } catch (e) {
                    console.warn('AdSense loading error:', e);
                  }
                }, 100);
              `));
            } else {
              newScript.appendChild(document.createTextNode(oldScript.innerHTML));
            }
          }
          
          // Script değiştirme işlemi
          if (oldScript.parentNode) {
            oldScript.parentNode.replaceChild(newScript, oldScript);
          }
        });
      }
      
      // Viewability tracking
      setTimeout(() => {
        if (adContainerRef.current) {
          const rect = adContainerRef.current.getBoundingClientRect();
          const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
          
          console.log(`Reklam render edildi (HTML): ${position}`, { 
            containerWidth: adContainerRef.current.offsetWidth,
            containerHeight: adContainerRef.current.offsetHeight,
            codeLength: adCode.length,
            isVisible,
            viewportPosition: {
              top: rect.top,
              bottom: rect.bottom,
              viewportHeight: window.innerHeight
            }
          });
        }
      }, 500);
    }
  }, [adCode]);

  // Reklamın konumuna göre özel stilendirme - AdSense optimizasyonu
  const getAdPositionStyles = () => {
    // Varsayılan stiller - AdSense viewability için optimize edildi
    const defaultStyles = {
      my: 3, 
      textAlign: 'center',
      '& ins': { 
        display: 'block',
        minHeight: '280px', // AdSense viewability için minimum yükseklik
        backgroundColor: '#f5f5f5' // Loading state görünümü
      },
      '& .adsbygoogle': {
        display: 'block',
        minHeight: '280px',
        backgroundColor: 'transparent'
      },
      border: showBorders || isTestAd ? '1px dashed #ccc' : 'none',
      padding: showBorders || isTestAd ? 2 : 1, // Hafif padding eklendi
      position: 'relative',
      minHeight: isTestAd ? '100px' : '280px', // AdSense için minimum yükseklik
      overflow: 'visible', // AdSense reklamlarının tam görünmesi için
      backgroundColor: showBorders ? 'rgba(0,0,0,0.02)' : 'transparent',
      borderRadius: 1, // Hafif yuvarlaklık
      boxSizing: 'border-box',
      ...containerStyles
    };

    // Pozisyona göre ek stiller
    if (position === 'MIDDLE_CONTENT') {
      return { 
        ...defaultStyles,
        my: 4,
        width: '100%',
        display: 'block',
        clear: 'both',
        padding: 1,
        borderRadius: 1,
        minHeight: '200px', // İçerik ortası reklamlar için minimum yükseklik arttırıldı
        '& iframe': { 
          width: '100%', 
          minHeight: '200px',
          border: 'none',
          display: 'block'
        },
        '& img': {
          maxWidth: '100%',
          margin: '0 auto',
          display: 'block'
        }
      };
    }
    
    if (position === 'SIDEBAR') {
      return {
        ...defaultStyles,
        my: 2,
        width: '100%',
        maxWidth: '300px', 
        mx: 'auto'
      };
    }

    return defaultStyles;
  };

  return (
    <Box sx={getAdPositionStyles()}>
      {isTestAd && (
        <Typography 
          variant="caption" 
          sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            bgcolor: 'info.main', 
            color: 'white', 
            px: 1,
            borderRadius: '0 0 4px 0'
          }}
        >
          Test Reklamı
        </Typography>
      )}
      {isUrlAd && (
        <Typography 
          variant="caption" 
          sx={{ 
            position: 'absolute', 
            top: 0, 
            right: 0, 
            bgcolor: 'success.main', 
            color: 'white', 
            px: 1,
            borderRadius: '0 0 0 4px'
          }}
        >
          Reklam
        </Typography>
      )}
      <Box ref={adContainerRef} className="ad-container" sx={{ width: '100%' }} />
    </Box>
  );
};

export default AdComponent; 